import { FC } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Theme } from "@mui/material/styles";
import { AvailablePaymentCurrency } from "@simplyk/common";

import { getPrizeCardElementId } from "../../helpers/constants";
import { TicketingPrizeOutput } from "../../types";
import { PrizeImage } from "../PrizeImage/PrizeImage";
import { PrizeValue } from "../PrizeValue/PrizeValue";

import { mergeSx } from "@/common/helpers/mergeSx";
import { unstyledButtonSx } from "@/common/helpers/unstyledButtonSx";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormTypography } from "@/forms-design-system/FormTypography";

export type PrizeCardVariant = "mobile" | "large" | "compact";

const CARD_PADDING = "11px";
const LARGE_IMAGE_MAX_SIZE = "153.5px";
const COMPACT_IMAGE_SIZE = "60.75px";
const MOBILE_IMAGE_SIZE = 120;
const TITLE_LINES: Record<PrizeCardVariant, number> = { mobile: 3, large: 4, compact: 1 };

interface PrizeCardProps {
  prize: TicketingPrizeOutput;
  currency: AvailablePaymentCurrency;
  variant: PrizeCardVariant;
  isDimmed?: boolean;
  onClick: () => void;
}

export const PrizeCard: FC<PrizeCardProps> = ({ prize, currency, variant, isDimmed = false, onClick }) => {
  const { t } = useTranslate();
  const isMobile = variant === "mobile";
  const isCompact = variant === "compact";

  return (
    <Box
      component="button"
      type="button"
      id={getPrizeCardElementId(prize.id)}
      onClick={onClick}
      aria-label={t("ticketing", "prizes.openDetails", { title: prize.title })}
      data-test="prize-card"
      sx={mergeSx(unstyledButtonSx, (theme: Theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        padding: CARD_PADDING,
        overflow: "hidden",
        textAlign: "left",
        border: `1px solid ${theme.palette.formsComponents.divider.borderColor}`,
        borderRadius: theme.radius(1),
        backgroundColor: isMobile ? theme.palette.forms.surface.quiet : "transparent",
        opacity: isDimmed ? 0.5 : 1,
        transition: theme.transitions.create(["background-color", "opacity"]),
        "&:hover": { backgroundColor: theme.palette.forms.surface.quiet },
      }))}
    >
      {prize.photoUrl && (
        <PrizeImage
          photoUrl={prize.photoUrl}
          alt={t("common", "a11y.itemPhoto", { item: prize.title })}
          sx={
            isMobile
              ? { width: MOBILE_IMAGE_SIZE, height: MOBILE_IMAGE_SIZE, flexShrink: 0 }
              : isCompact
                ? { width: COMPACT_IMAGE_SIZE, height: COMPACT_IMAGE_SIZE, flexShrink: 0 }
                : { flex: "1 0 0", minWidth: 0, maxWidth: LARGE_IMAGE_MAX_SIZE, maxHeight: LARGE_IMAGE_MAX_SIZE }
          }
        />
      )}
      <Stack
        gap={isMobile || isCompact ? 0.25 : 0.5}
        justifyContent="center"
        sx={{ flex: "1 0 0", minWidth: 0, minHeight: isCompact ? COMPACT_IMAGE_SIZE : undefined }}
      >
        <FormTypography
          variant="h6"
          vibe="intense"
          sx={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: TITLE_LINES[variant],
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {prize.title}
        </FormTypography>
        {prize.value !== null && <PrizeValue value={prize.value} currency={currency} />}
      </Stack>
    </Box>
  );
};
